package app.nestbridge.app.di

import com.karawek.core.auth.api.AuthSessionManager
import com.karawek.core.network.AuthClientPlugin
import com.karawek.core.network.SessionExpiredException
import app.nestbridge.config.BuildKonfig
import com.karawek.core.platform.createHttpClientEngine
import io.ktor.client.HttpClient
import io.ktor.client.plugins.HttpRequestRetry
import io.ktor.client.plugins.HttpResponseValidator
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.client.plugins.logging.LogLevel
import io.ktor.client.plugins.logging.Logging
import io.ktor.http.HttpStatusCode
import io.ktor.serialization.kotlinx.json.json
import kotlinx.serialization.json.Json
import kotlin.coroutines.cancellation.CancellationException
import me.tatarka.inject.annotations.Provides
import software.amazon.lastmile.kotlin.inject.anvil.AppScope
import software.amazon.lastmile.kotlin.inject.anvil.ContributesTo
import software.amazon.lastmile.kotlin.inject.anvil.SingleIn

@ContributesTo(AppScope::class)
interface NetworkModule {

    @SingleIn(AppScope::class)
    @Provides
    fun provideHttpClient(
        authSessionManager: AuthSessionManager,
    ): HttpClient =
        HttpClient(createHttpClientEngine()) {

            if (BuildKonfig.httpMaxRetries > 0) {
                install(HttpRequestRetry) {
                    // Retry transient 5xx (e.g. Cloud Run cold-start 503s) AND
                    // transient network failures (cold-start socket timeouts),
                    // but never retry a cancellation / session-expiry signal.
                    retryOnServerErrors(maxRetries = BuildKonfig.httpMaxRetries)
                    retryOnExceptionIf(maxRetries = BuildKonfig.httpMaxRetries) { _, cause ->
                        cause !is CancellationException
                    }
                    exponentialDelay()
                }
            }

            install(Logging) {
                level = LogLevel.valueOf(BuildKonfig.httpLogLevel)
            }

            install(ContentNegotiation) {
                json(
                    Json {
                        ignoreUnknownKeys = true
                        explicitNulls = false
                    },
                )
            }

            // Central auth-expiry handling: any 401 means the session is gone
            // server-side. Clear local state (so the auth gate resets to login)
            // and abort the call as a cancellation, so callers never crash on it.
            // Runs before each API client's own status check.
            HttpResponseValidator {
                validateResponse { response ->
                    if (response.status == HttpStatusCode.Unauthorized) {
                        authSessionManager.invalidateLocal()
                        throw SessionExpiredException()
                    }
                }
            }

            install(AuthClientPlugin) {
                this.authSessionManager = authSessionManager
            }
        }
}
